import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Clock, MessageSquare, CheckCircle, MapPin, Activity, Heart } from 'lucide-react';

interface Resident {
  name: string;
  age: number;
  address: string;
  emergencyContact: string;
  careLevel: string;
  primaryConditions: string[];
}

interface Caregiver {
  id: string;
  name: string;
  role: string;
  lastAccess: Date;
}

interface CaregiverDashboardProps {
  resident: Resident;
  loggedInUser: Caregiver;
}

const CaregiverDashboard: React.FC<CaregiverDashboardProps> = ({ resident, loggedInUser }) => {
  // Mock data for the dashboard
  const recentAlerts = [
    {
      id: 'alert-1',
      type: 'FALL_RISK',
      priority: 'HIGH',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      description: 'Increased unsteady gait detected in bathroom',
      status: 'ACTIVE',
      assignedTo: 'Lisa Rodriguez'
    },
    {
      id: 'alert-2',
      type: 'MISSED_MEDICATION',
      priority: 'MEDIUM',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
      description: 'Morning medication not taken',
      status: 'RESOLVED',
      assignedTo: 'Michael Chen',
      resolution: 'Medication administered at 10:45 AM'
    },
    {
      id: 'alert-3',
      type: 'INACTIVITY',
      priority: 'MEDIUM',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
      description: 'Reduced movement detected since yesterday',
      status: 'ACTIVE',
      assignedTo: null
    }
  ];

  const careNotes = [
    {
      id: 'note-1',
      author: 'Lisa Rodriguez',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      content: 'Administered medication. Resident reported slight dizziness afterward, will monitor.',
      type: 'MEDICATION'
    },
    {
      id: 'note-2',
      author: 'Robert Wilson',
      timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000), // 26 hours ago
      content: 'Completed physical therapy session. Progress on left leg mobility, exercises provided for daily practice.',
      type: 'THERAPY'
    },
    {
      id: 'note-3',
      author: 'Dr. Sarah Johnson',
      timestamp: new Date(Date.now() - 50 * 60 * 60 * 1000), // 50 hours ago
      content: 'Blood pressure readings are stable. Continue current medication regimen and daily BP monitoring.',
      type: 'MEDICAL'
    }
  ];

  const checkIns = [
    {
      id: 'checkin-1',
      caregiver: 'Lisa Rodriguez',
      type: 'In-person',
      scheduledFor: new Date(Date.now() + 3 * 60 * 60 * 1000), // 3 hours from now
      status: 'SCHEDULED',
      tasks: ['Medication administration', 'Vital signs check', 'Meal preparation']
    },
    {
      id: 'checkin-2',
      caregiver: 'Michael Chen',
      type: 'Video call',
      scheduledFor: new Date(Date.now() + 8 * 60 * 60 * 1000), // 8 hours from now
      status: 'SCHEDULED',
      tasks: ['Evening check-in', 'Social support']
    }
  ];

  const activityData = [
    { day: 'Monday', steps: 2100, roomChanges: 32, baseline: 2500 },
    { day: 'Tuesday', steps: 1800, roomChanges: 28, baseline: 2500 },
    { day: 'Wednesday', steps: 2300, roomChanges: 35, baseline: 2500 },
    { day: 'Thursday', steps: 1900, roomChanges: 30, baseline: 2500 },
    { day: 'Friday', steps: 1700, roomChanges: 25, baseline: 2500 },
    { day: 'Saturday', steps: 1500, roomChanges: 22, baseline: 2500 },
    { day: 'Sunday', steps: 1400, roomChanges: 20, baseline: 2500 }
  ];

  const currentLocation = {
    room: 'Living Room',
    lastMovement: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
    duration: '45 minutes'
  };

  const vitalSigns = {
    heartRate: 72,
    lastMeasured: new Date(Date.now() - 60 * 60 * 1000), // 1 hour ago
    bloodPressure: '128/82',
    restingActivity: 'Normal'
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return `Today at ${formatTime(date)}`;
    } else if (diffInDays === 1) {
      return `Yesterday at ${formatTime(date)}`;
    } else {
      return date.toLocaleDateString() + ' at ' + formatTime(date);
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'FALL_RISK':
        return <AlertTriangle className="text-red-500" />;
      case 'MISSED_MEDICATION':
        return <Clock className="text-yellow-500" />;
      case 'INACTIVITY':
        return <Activity className="text-orange-500" />;
      default:
        return <AlertTriangle className="text-gray-500" />;
    }
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
        {/* Current Location */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center mb-2">
            <MapPin className="text-blue-500 mr-2" />
            <h3 className="text-lg font-medium">Current Location</h3>
          </div>
          <p className="text-2xl font-bold mb-1">{currentLocation.room}</p>
          <p className="text-sm text-gray-600">
            Last movement: {formatTime(currentLocation.lastMovement)}
          </p>
          <p className="text-sm text-gray-600">
            In this location for: {currentLocation.duration}
          </p>
        </div>

        {/* Activity Level */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center mb-2">
            <Activity className="text-green-500 mr-2" />
            <h3 className="text-lg font-medium">Activity Level</h3>
          </div>
          <p className="text-2xl font-bold mb-1">{activityData[activityData.length - 1].steps} steps</p>
          <p className="text-sm text-gray-600">
            Today's room changes: {activityData[activityData.length - 1].roomChanges}
          </p>
          <p className="text-sm text-gray-600">
            {activityData[activityData.length - 1].steps < activityData[activityData.length - 1].baseline 
              ? `${Math.round((activityData[activityData.length - 1].baseline - activityData[activityData.length - 1].steps) / activityData[activityData.length - 1].baseline * 100)}% below baseline`
              : `${Math.round((activityData[activityData.length - 1].steps - activityData[activityData.length - 1].baseline) / activityData[activityData.length - 1].baseline * 100)}% above baseline`
            }
          </p>
        </div>

        {/* Vital Signs */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center mb-2">
            <Heart className="text-red-500 mr-2" />
            <h3 className="text-lg font-medium">Vital Signs</h3>
          </div>
          <p className="text-2xl font-bold mb-1">{vitalSigns.heartRate} BPM</p>
          <p className="text-sm text-gray-600">
            BP: {vitalSigns.bloodPressure} | Status: {vitalSigns.restingActivity}
          </p>
          <p className="text-sm text-gray-600">
            Last measured: {formatTime(vitalSigns.lastMeasured)}
          </p>
        </div>
      </div>

      {/* Weekly Activity */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium">Weekly Activity</h3>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="steps" 
                stroke="#3b82f6" 
                name="Daily Steps"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="baseline" 
                stroke="#9ca3af" 
                name="Baseline"
                strokeWidth={1}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-medium">Recent Alerts</h3>
            <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">
              {recentAlerts.filter(alert => alert.status === 'ACTIVE').length} Active
            </span>
          </div>
          <div className="p-2">
            {recentAlerts.map(alert => (
              <div key={alert.id} className={`p-3 mb-2 rounded-lg ${
                alert.status === 'ACTIVE' 
                  ? 'bg-red-50 border-l-4 border-red-500' 
                  : 'bg-gray-50'
              }`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3 mt-1">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <p className="font-medium">{alert.description}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        alert.priority === 'HIGH' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {alert.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDate(alert.timestamp)}
                    </p>
                    <div className="flex justify-between mt-2">
                      <span className="text-sm text-gray-500">
                        {alert.assignedTo ? `Assigned to: ${alert.assignedTo}` : 'Unassigned'}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        alert.status === 'ACTIVE' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {alert.status}
                      </span>
                    </div>
                    {alert.resolution && (
                      <p className="text-sm text-gray-600 mt-1 bg-green-50 p-1 rounded">
                        <CheckCircle className="inline-block text-green-500 mr-1" size={16} />
                        {alert.resolution}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Check-ins and Care Notes */}
        <div className="space-y-6">
          {/* Upcoming Check-ins */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="text-lg font-medium">Upcoming Check-ins</h3>
            </div>
            <div className="p-2">
              {checkIns.map(checkIn => (
                <div key={checkIn.id} className="p-3 mb-2 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <div className="flex justify-between">
                    <p className="font-medium">{checkIn.type} check-in with {checkIn.caregiver}</p>
                    <span className="text-sm text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full">
                      {checkIn.scheduledFor.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {checkIn.scheduledFor.toLocaleDateString()}
                  </p>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Tasks:</p>
                    <ul className="text-sm text-gray-600 ml-5 list-disc">
                      {checkIn.tasks.map((task, index) => (
                        <li key={index}>{task}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Care Notes */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="text-lg font-medium">Recent Care Notes</h3>
            </div>
            <div className="p-2">
              {careNotes.map(note => (
                <div key={note.id} className="p-3 mb-2 bg-gray-50 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <MessageSquare className="text-gray-500" />
                    </div>
                    <div>
                      <div className="flex items-baseline">
                        <p className="font-medium mr-2">{note.author}</p>
                        <span className="text-xs text-gray-500">
                          {formatDate(note.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{note.content}</p>
                      <span className="inline-block text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded mt-2">
                        {note.type}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverDashboard; 